import { doc, type DocumentReference, type Firestore, type Transaction } from 'firebase/firestore';
import type { Day, Expense, TripConfig, Transport } from '../types';
import { destinationDates, destinationForDate, SHARED_ITEM_FIELDS } from './destinationItems';
import { readDestinationDocs } from './destinationWrites';
import { subcatEmoji } from '../constants/taxonomy';
import { expenseBaseCurrency } from './expenseUtils';
import { secondaryChargeRefs, type ExpenseSourceType } from './expenseLinks';
import { assertLinkedCostUnchanged } from './bookingCostReview';
import { claimFullCost } from './fullCostProtocol';

import { stripUndef, equal, conflict, mergeValue, mergeItems } from "../../functions/src/recordMerge.mjs";
export { stripUndef, equal, conflict, mergeValue, mergeItems } from "../../functions/src/recordMerge.mjs";
type Item = { id?: string; [key: string]: unknown };

/** Merge one booking by identity, including an unambiguous legacy ID-less row. */
export function mergeBookingChange<T extends { id?: string }>(stored: T[], before: T | null, after: T | null): { items: T[]; saved: T | null } {
  // Form intent and UI routing fields must never become booking data.
  const clean = (value: T | null): Item | null => value && stripUndef(
    Object.fromEntries(Object.entries(value).filter(([key]) => !key.startsWith('_'))),
  );
  const previous = clean(before);
  const next = clean(after);
  if (next && (!next.id || (previous?.id && previous.id !== next.id))) conflict();
  const latest = stored.map(value => clean(value)!);
  let index = -1;
  let saved: Item | null = next;
  if (previous?.id) {
    const matches = latest.flatMap((item, i) => item.id === previous.id ? [i] : []);
    if (matches.length > 1) conflict();
    index = matches[0] ?? -1;
    if (index < 0) {
      if (!next) return { items: stored, saved: null }; // Already deleted; never resurrect an edited row.
      conflict();
    }
    saved = next ? mergeItems([latest[index]], [previous], [next])[0] : null;
    if (!next && !equal(latest[index], previous)) conflict();
  } else if (previous) {
    const matches = latest.flatMap((item, i) => !item.id && equal(item, previous) ? [i] : []);
    if (matches.length !== 1) {
      // A successfully committed legacy upgrade can be retried with the same ID.
      if (!matches.length && next && latest.filter(item => item.id === next.id).length === 1
        && latest.some(item => equal(item, next))) return { items: stored, saved: next as T };
      conflict();
    }
    index = matches[0];
    if (next && latest.some(item => item.id === next.id)) conflict();
  } else if (next) {
    const matches = latest.filter(item => item.id === next.id);
    if (matches.length) {
      if (matches.length === 1 && equal(matches[0], next)) return { items: stored, saved: matches[0] as T };
      conflict();
    }
  } else return { items: stored, saved: null };

  const items = stored.filter((_, i) => i !== index);
  if (saved) items.splice(index < 0 ? items.length : index, 0, saved as T);
  return { items, saved: saved as T | null };
}

const departureMs = (item: Transport) => {
  const dep = typeof item.dep === 'string' ? item.dep : '';
  return dep ? new Date(dep.includes('T') ? dep : `1970-01-01T${dep}`).getTime() : 0;
};
const byDeparture = (items: Transport[]) => [...items].sort((a, b) => departureMs(a) - departureMs(b));

/** The day a leg is filed under: its departure date. Both importers,
 *  docs/parsing-schema.md and destinationRemoval.ts already assume this; only
 *  the manual add/edit path didn't, because it filed under whichever day the
 *  form happened to be opened from. That matters most for an overnight leg,
 *  which is added from the *arrival* day's gap row — it would land on the
 *  wrong document and then vanish from the very row that added it, since that
 *  row lists by departure date. Falls back to the caller's day when `dep`
 *  carries no date of its own (empty, or a bare time). */
const filingDate = (next: Transport | null, fallback: string) => {
  const dep = typeof next?.dep === 'string' ? next.dep : '';
  return /^\d{4}-\d{2}-\d{2}/.test(dep) ? dep.slice(0, 10) : fallback;
};

/** Edit one transport without propagating it to other dates. */
export async function writeTransportChange(transaction: Transaction, db: Firestore,
  input: { tripId: string; date: string; previous: Transport | null; next: Transport | null }): Promise<Transport | null> {
  const target = filingDate(input.next, input.date);
  const dayRef = (date: string) => doc(db, 'trips', input.tripId, 'itinerary', date);
  const ref = dayRef(input.date);

  if (target === input.date) {
    const snapshot = await transaction.get(ref);
    const stored: Transport[] = snapshot.data()?.transports ?? [];
    const { items: transports, saved } = mergeBookingChange(stored, input.previous, input.next);
    if (transports === stored) return saved;
    transaction.set(ref, { transports: byDeparture(transports) }, { merge: true });
    return saved;
  }

  // Re-filing crosses two documents, so it's two merges rather than one: a
  // removal on the day it was on and an insert on the day it belongs to, in
  // the one transaction. Reads first — transactions require it.
  const toRef = dayRef(target);
  const fromSnapshot = await transaction.get(ref);
  const toSnapshot = await transaction.get(toRef);
  const fromStored: Transport[] = fromSnapshot.data()?.transports ?? [];
  const { items: fromItems } = mergeBookingChange(fromStored, input.previous, null);
  // mergeBookingChange treats "the row I meant to edit isn't here" as an
  // already-deleted no-op, which is right for an edit in place but would
  // resurrect the row on the new day here. Someone else moved or removed it.
  if (input.previous && fromItems === fromStored) conflict();
  const { items: toItems, saved } = mergeBookingChange(toSnapshot.data()?.transports ?? [], null, input.next);
  if (fromItems !== fromStored) transaction.set(ref, { transports: byDeparture(fromItems) }, { merge: true });
  transaction.set(toRef, { transports: byDeparture(toItems) }, { merge: true });
  return saved;
}

/** Apply only the user's delta, atomically across this visit; never overwrite a stale day. */
export async function writeDayChanges(transaction: Transaction, db: Firestore,
  input: { tripId: string; previous: Day; updated: Day; userId?: string; expenses?: Expense[] }) {
  const { tripId } = input;
  const previous = stripUndef(input.previous);
  const updated = stripUndef(input.updated);
  if (previous.date !== updated.date) throw new Error('Moving a day requires a separate itinerary change.');
  const tripSnapshot = await transaction.get(doc(db, 'trips', tripId));
  if (!tripSnapshot.exists()) throw new Error('This trip is no longer available.');
  const trip = tripSnapshot.data() as TripConfig;
  const days = await readDestinationDocs(transaction, db, tripId, destinationDates(trip, updated.date));
  const selected = days.find(day => day.date === updated.date)!;
  const selectedChanges: Record<string, unknown> = {};
  const sharedChanges: Record<string, unknown> = {};
  const inherited = destinationForDate(trip.destinations ?? [], updated.date)?.travellerIds
    ?? (trip.travellers ?? []).map(traveller => traveller.id);
  const travellersFor = (data: Record<string, any>) => data.travellers?.length ? data.travellers : inherited;
  for (const field of [...SHARED_ITEM_FIELDS, 'transports'] as const) {
    const before = (previous[field] ?? []) as Item[];
    const after = (updated[field] ?? []) as Item[];
    if (equal(before, after)) continue;
    selectedChanges[field] = mergeItems(selected.data[field] ?? [], before, after);
    if (field !== 'transports') sharedChanges[field] = selectedChanges[field];
  }
  if (!equal(previous.travellers, updated.travellers)) {
    selectedChanges.travellers = mergeValue(travellersFor(selected.data), previous.travellers, updated.travellers);
    sharedChanges.travellers = selectedChanges.travellers;
  }
  if (!equal(previous.accommodation, updated.accommodation)) {
    const current = selected.data.accommodation ?? (previous.accommodation?.name ? undefined : previous.accommodation);
    selectedChanges.accommodation = mergeValue(current, previous.accommodation, updated.accommodation);
  }
  const writes = days.map(day => {
    if (day === selected) return { ...day, changes: selectedChanges };
    const changes: Record<string, unknown> = {};
    for (const field of SHARED_ITEM_FIELDS) {
      if (!(field in sharedChanges)) continue;
      const seed = sharedChanges[field] as Item[];
      changes[field] = mergeItems(day.data[field] ?? seed, (previous[field] ?? []) as Item[], (updated[field] ?? []) as Item[], seed);
    }
    if ('travellers' in sharedChanges) changes.travellers = mergeValue(
      travellersFor(day.data), previous.travellers, updated.travellers);
    return { ...day, changes };
  });
  // Source changes and linked financial records commit together. Money remains
  // owned by Expenses: this path only creates a new cost or syncs source metadata.
  const expenseWrites: { ref: DocumentReference; data: Record<string, unknown>; merge: boolean }[] = [];
  const prepared = new Map((input.expenses || []).map(expense => [expense.id, expense]));
  if (prepared.size !== (input.expenses || []).length) conflict();
  const used = new Set<string>();
  for (const field of SHARED_ITEM_FIELDS) {
    if (!(field in sharedChanges)) continue;
    const before = (previous[field] || []) as Item[];
    const after = (updated[field] || []) as Item[];
    const merged = sharedChanges[field] as Item[];
    const sourceType = { attractions: 'attraction', eats: 'food', nightlife: 'nightlife', shopping: 'shopping', notes: 'other' }[field];
    for (const id of new Set([...before, ...after].map(item => item.id))) {
      const old = before.find(item => item.id === id);
      const proposed = after.find(item => item.id === id);
      if (equal(old, proposed)) continue;
      const saved = merged.find(item => item.id === id);
      if (old && !saved && input.userId) {
        for (const ref of await secondaryChargeRefs(transaction, db, tripId, old,
          sourceType as ExpenseSourceType)) {
          expenseWrites.push({ ref, merge: true, data: { itineraryDeleted: true,
            itineraryDeletedBy: input.userId, itineraryDeletedAt: new Date().toISOString() } });
        }
      }
      const expenseId = saved?.expenseLogId || old?.expenseLogId;
      if (typeof expenseId !== 'string' || !expenseId) continue;
      if (used.has(expenseId)) conflict(); // One financial record cannot serve two different sources.
      used.add(expenseId);
      const expense = prepared.get(expenseId);
      const ref = doc(db, 'trips', tripId, 'expenses', expenseId);
      const snapshot = await transaction.get(ref);
      const existing = snapshot.exists() ? snapshot.data() as Expense : null;
      assertLinkedCostUnchanged(existing, old, proposed);
      const stored = (selected.data[field] || []).find((item: Item) => item.id === id) as Item | undefined;
      const catName = saved && String(field === 'eats' ? saved.meal || 'Restaurant'
        : field === 'shopping' ? 'Shopping' : field === 'notes' ? 'Other'
        : saved.category || (field === 'nightlife' ? 'Other' : 'Attraction'));
      const metadata = saved && { desc: saved.name || '', catName,
        cat: field === 'shopping' ? '🛍️' : field === 'notes' ? '💲'
          : subcatEmoji(catName!, field === 'attractions' ? 'experiences' : sourceType) };
      if (expense && (expense.sourceId !== id || expense.sourceType !== sourceType || !saved
        || (old?.expenseLogId && old.expenseLogId !== expenseId))) conflict();
      if (existing) {
        if ((existing.sourceId && existing.sourceId !== id) || (existing.sourceType && existing.sourceType !== sourceType
          && !(sourceType === 'other' && existing.sourceType === 'note'))
          || (!old?.expenseLogId && (existing.sourceId !== id || existing.sourceType !== sourceType))
          || (!old && existing.itineraryDeleted)) conflict();
        if (!saved) {
          if (!input.userId) throw new Error('Sign in again before removing this item.');
          expenseWrites.push({ ref, merge: true, data: { itineraryDeleted: true,
            itineraryDeletedBy: input.userId, itineraryDeletedAt: new Date().toISOString() } });
        } else if (existing.itineraryDeleted || Object.entries(metadata!).some(([key, value]) => existing[key as keyof Expense] !== value)) {
          expenseWrites.push({ ref, merge: true, data: { ...metadata,
            ...(existing.itineraryDeleted ? { itineraryDeleted: false } : {}) } });
        }
      } else if (expense) {
        if (expense.amountBasis === 'full-cost') {
          for (const day of days) {
            const current = (day.data[field] || []).find((item: Item) => item.id === id);
            if (current && (await secondaryChargeRefs(transaction, db, tripId, current, sourceType as ExpenseSourceType)).length) {
              throw new Error('This booking already has linked expenses. Review those charges before logging its full cost.');
            }
          }
        }
        const active = (trip.travellers || []).filter(t => t.inviteStatus !== 'pending').map(t => t.id);
        if (old?.expenseLogId || stored?.expenseLogId
          || expense.targetCurrency !== expenseBaseCurrency(trip)
          || !active.includes(expense.paid_by) || !expense.split.length || expense.split.some(id => !active.includes(id))) {
          throw new Error('The trip or expense link changed. Reopen this item before logging its cost.');
        }
        expenseWrites.push({ ref, merge: false, data: stripUndef({ ...expense, ...metadata, importedItemDate: updated.date }) as unknown as Record<string, unknown> });
      }
    }
  }
  if ([...prepared.keys()].some(id => !used.has(id))) conflict();
  for (const { ref, changes } of writes) {
    if (Object.keys(changes).length) transaction.set(ref, stripUndef(changes), { merge: true });
  }
  for (const { ref, data, merge } of expenseWrites) transaction.set(ref,
    merge ? data : { ...claimFullCost(transaction, db, tripId, data as unknown as Expense) }, { merge });
}
